import { keepPreviousData, useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useParams } from 'react-router';

import {
  DateRangeFilter,
  LIST_FILTER_DATE_RANGE_PRESETS,
  type DateRange,
} from '@/components/shared/date-range-filter';
import { EntityCombobox } from '@/components/shared/entity-combobox';
import { FilterBar } from '@/components/shared/filter-bar';
import { MinisterCombobox } from '@/components/shared/minister-combobox';
import { DataTable, type DataTableColumn } from '@/components/shared/data-table';
import { MemberNameLink } from '@/components/shared/member-name-link';
import { FormDialog } from '@/components/shared/form-dialog';
import { FormField } from '@/components/shared/form-field';
import { PageHeader } from '@/components/shared/page-header';
import { Pagination } from '@/components/shared/pagination';
import { SearchInput } from '@/components/shared/search-input';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { DatePicker } from '@/components/ui/date-picker';
import { Input } from '@/components/ui/input';
import { Listbox } from '@/components/ui/listbox';
import { Textarea } from '@/components/ui/textarea';
import { toast } from '@/components/ui/toast';
import { api, type OffsetPage } from '@/lib/api';
import { resolveApiError } from '@/lib/api-errors';
import { hasPermission, PERMISSIONS, useCurrentMembership } from '@/lib/permissions';
import { churchToday, formatDate } from '@/utils/format';
import { useMemberPicker } from '@/pages/pastoral/use-member-picker';

import { MarriageMinistersFields, MarriageWitnessPair } from './marriage-party-fields';
import {
  emptyMinister,
  emptyWitnessPair,
  ministerDraftsFrom,
  witnessDraftsFrom,
  type MinisterDraft,
  type WitnessDraft,
} from './marriage-party-state';
import { SacramentImagesFields } from './sacrament-images';
import { SacramentPersonDetailsDialog } from './sacrament-person-details-dialog';
import { SacramentRegisterDetailDialog } from './sacrament-register-detail-dialog';
import {
  certificateFor,
  dateFor,
  funeralDateFor,
  marriageMinisterNames,
  type RegisterKind,
  type RegisterRow,
} from './sacrament-register-types';
import { SacramentsSubnav } from './sacraments-subnav';
import {
  composeSacramentPersonName,
  emptySacramentPersonDetails,
  emptySacramentPersonRow,
  SacramentPeopleFields,
  sacramentPersonHasDetails,
  splitSacramentPersonName,
  type SacramentPersonDetails,
  type SacramentPersonRow,
} from './sacrament-people-fields';

const revokePreview = (url: string | null) => {
  if (url?.startsWith('blob:')) URL.revokeObjectURL(url);
};

const detailsPayload = (details: SacramentPersonDetails) => ({
  motherMemberId: details.motherMemberId,
  motherName: details.motherName.trim() || null,
  fatherMemberId: details.fatherMemberId,
  fatherName: details.fatherName.trim() || null,
  photoKey: details.photoKey,
  certificatePhotoKey: details.certificatePhotoKey,
});

export type { RegisterKind, RegisterRow };

const supportsMultiplePeople = (kind: RegisterKind) =>
  kind === 'baptisms' || kind === 'confirmations';

const supportsCertificateFilter = (kind: RegisterKind) => kind !== 'funerals';

const emptyDateRange = (): DateRange => ({ preset: 'allTime', from: null, to: null });

const REGISTER_KINDS: RegisterKind[] = [
  'baptisms',
  'confirmations',
  'marriages',
  'funerals',
  'transfers',
];

export const SacramentRegisterPage = ({ kind: kindProp }: { kind?: RegisterKind }) => {
  const params = useParams();
  const kindFromRoute = REGISTER_KINDS.includes(params.registerKind as RegisterKind)
    ? (params.registerKind as RegisterKind)
    : 'baptisms';
  const kind = kindProp ?? kindFromRoute;

  const { t } = useTranslation('sacraments');
  const { t: tCommon } = useTranslation();
  const { t: tErrors } = useTranslation('errors');
  const queryClient = useQueryClient();
  const membership = useCurrentMembership();
  const canRecord = hasPermission(membership, PERMISSIONS.SACRAMENTS_RECORD);
  const canEdit = hasPermission(membership, PERMISSIONS.SACRAMENTS_EDIT);
  const canCreatePeople = hasPermission(membership, PERMISSIONS.PEOPLE_CREATE);

  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(25);
  const [search, setSearch] = useState('');
  const [certificateQuery, setCertificateQuery] = useState('');
  const [filterDateRange, setFilterDateRange] = useState<DateRange>(emptyDateRange);
  const [open, setOpen] = useState(false);
  const [detailId, setDetailId] = useState<string | null>(null);
  const [editingId, setEditingId] = useState<string | null>(null);

  const memberPicker = useMemberPicker(open && kind !== 'marriages', `sacrament-${kind}`);
  const spouseOnePicker = useMemberPicker(open && kind === 'marriages', 'sacrament-spouse-one');
  const spouseTwoPicker = useMemberPicker(open && kind === 'marriages', 'sacrament-spouse-two');

  const [peopleRows, setPeopleRows] = useState<SacramentPersonRow[]>([emptySacramentPersonRow()]);
  const [editDetails, setEditDetails] = useState<SacramentPersonDetails>(emptySacramentPersonDetails);
  const [detailsRowKey, setDetailsRowKey] = useState<string | null>(null);
  const [editDetailsOpen, setEditDetailsOpen] = useState(false);

  const [memberId, setMemberId] = useState<string | null>(null);
  const [spouseOneMemberId, setSpouseOneMemberId] = useState<string | null>(null);
  const [spouseTwoMemberId, setSpouseTwoMemberId] = useState<string | null>(null);
  const [personName, setPersonName] = useState('');
  const [firstName, setFirstName] = useState('');
  const [middleName, setMiddleName] = useState('');
  const [lastName, setLastName] = useState('');
  const [spouseOneName, setSpouseOneName] = useState('');
  const [spouseTwoName, setSpouseTwoName] = useState('');
  const [spouseOneWitnesses, setSpouseOneWitnesses] = useState<[WitnessDraft, WitnessDraft]>(
    emptyWitnessPair,
  );
  const [spouseTwoWitnesses, setSpouseTwoWitnesses] = useState<[WitnessDraft, WitnessDraft]>(
    emptyWitnessPair,
  );
  const [ministers, setMinisters] = useState<MinisterDraft[]>(() => [emptyMinister()]);
  const [eventDate, setEventDate] = useState<string | null>(churchToday());
  const [place, setPlace] = useState('');
  const [ministerName, setMinisterName] = useState('');
  const [certificateNumber, setCertificateNumber] = useState('');
  const [baptismType, setBaptismType] = useState<'infant' | 'adult' | 'unknown'>('unknown');
  const [marriageType, setMarriageType] = useState<'church' | 'civil' | 'customary' | 'other'>(
    'church',
  );
  const [direction, setDirection] = useState<'in' | 'out'>('in');
  const [otherChurchName, setOtherChurchName] = useState('');
  const [dateOfDeath, setDateOfDeath] = useState<string | null>(null);
  const [notes, setNotes] = useState('');

  const list = useQuery({
    queryKey: [
      'sacraments',
      kind,
      page,
      pageSize,
      search,
      certificateQuery,
      filterDateRange.from,
      filterDateRange.to,
    ],
    queryFn: () =>
      api.get<{ data: RegisterRow[]; page: OffsetPage }>(`/sacraments/${kind}`, {
        query: {
          page,
          pageSize,
          q: search.trim() || undefined,
          dateFrom: filterDateRange.from || undefined,
          dateTo: filterDateRange.to || undefined,
          certificateNumber:
            supportsCertificateFilter(kind) && certificateQuery.trim()
              ? certificateQuery.trim()
              : undefined,
        },
      }),
    placeholderData: keepPreviousData,
  });

  const reset = () => {
    setPeopleRows((rows) => {
      rows.forEach((row) => {
        revokePreview(row.photoPreviewUrl);
        revokePreview(row.certificatePhotoPreviewUrl);
      });
      return [emptySacramentPersonRow()];
    });
    setEditDetails((current) => {
      revokePreview(current.photoPreviewUrl);
      revokePreview(current.certificatePhotoPreviewUrl);
      return emptySacramentPersonDetails();
    });
    setDetailsRowKey(null);
    setEditDetailsOpen(false);
    setEditingId(null);
    setMemberId(null);
    setSpouseOneMemberId(null);
    setSpouseTwoMemberId(null);
    memberPicker.setSelected(null);
    spouseOnePicker.setSelected(null);
    spouseTwoPicker.setSelected(null);
    setPersonName('');
    setFirstName('');
    setMiddleName('');
    setLastName('');
    setSpouseOneName('');
    setSpouseTwoName('');
    setSpouseOneWitnesses(emptyWitnessPair());
    setSpouseTwoWitnesses(emptyWitnessPair());
    setMinisters([emptyMinister()]);
    setEventDate(churchToday());
    setPlace('');
    setMinisterName('');
    setCertificateNumber('');
    setBaptismType('unknown');
    setMarriageType('church');
    setDirection('in');
    setOtherChurchName('');
    setDateOfDeath(kind === 'funerals' ? churchToday() : null);
    setNotes('');
  };

  useEffect(() => {
    setPage(1);
    setSearch('');
    setCertificateQuery('');
    setFilterDateRange(emptyDateRange());
    setOpen(false);
    setDetailId(null);
    reset();
    // eslint-disable-next-line react-hooks/exhaustive-deps -- reset pickers when register kind changes
  }, [kind]);

  const body = useMemo(() => {
    const sharedNotes = notes.trim() || null;
    switch (kind) {
      case 'baptisms':
        return editingId
          ? {
              memberId,
              firstName: firstName.trim() || undefined,
              middleName: middleName.trim() || null,
              lastName: lastName.trim() || undefined,
              baptismDate: eventDate,
              place: place.trim() || null,
              ministerName: ministerName.trim() || null,
              baptismType,
              baptismNumber: certificateNumber.trim() || null,
              notes: sharedNotes,
              ...detailsPayload(editDetails),
            }
          : {
              baptismDate: eventDate,
              place: place.trim() || null,
              ministerName: ministerName.trim() || null,
              baptismType,
              notes: sharedNotes,
              people: peopleRows.map((row) => ({
                memberId: row.memberId,
                firstName: row.firstName.trim() || undefined,
                middleName: row.middleName.trim() || null,
                lastName: row.lastName.trim() || undefined,
                baptismNumber: row.certificateNumber.trim() || null,
                createMember: canCreatePeople && row.createMember && !row.memberId,
                ...detailsPayload(row),
              })),
            };
      case 'confirmations':
        return editingId
          ? {
              memberId,
              firstName: firstName.trim() || undefined,
              middleName: middleName.trim() || null,
              lastName: lastName.trim() || undefined,
              confirmationDate: eventDate,
              place: place.trim() || null,
              ministerName: ministerName.trim() || null,
              confirmationNumber: certificateNumber.trim() || null,
              notes: sharedNotes,
              ...detailsPayload(editDetails),
            }
          : {
              confirmationDate: eventDate,
              place: place.trim() || null,
              ministerName: ministerName.trim() || null,
              notes: sharedNotes,
              people: peopleRows.map((row) => ({
                memberId: row.memberId,
                firstName: row.firstName.trim() || undefined,
                middleName: row.middleName.trim() || null,
                lastName: row.lastName.trim() || undefined,
                confirmationNumber: row.certificateNumber.trim() || null,
                createMember: canCreatePeople && row.createMember && !row.memberId,
                ...detailsPayload(row),
              })),
            };
      case 'marriages':
        return {
          spouseOneMemberId,
          spouseTwoMemberId,
          spouseOneName: spouseOneName.trim() || undefined,
          spouseTwoName: spouseTwoName.trim() || undefined,
          spouseOneWitnesses: spouseOneWitnesses.map((witness) => ({
            memberId: witness.memberId,
            name: witness.name.trim() || null,
          })),
          spouseTwoWitnesses: spouseTwoWitnesses.map((witness) => ({
            memberId: witness.memberId,
            name: witness.name.trim() || null,
          })),
          marriageDate: eventDate,
          place: place.trim() || null,
          officiatingMinisters: ministers.map((minister) => minister.name.trim()).filter(Boolean),
          certificateNumber: certificateNumber.trim() || null,
          marriageType,
          notes: sharedNotes,
        };
      case 'funerals':
        return {
          memberId,
          personName: personName.trim() || undefined,
          serviceDate: eventDate,
          dateOfDeath,
          place: place.trim() || null,
          officiantName: ministerName.trim() || null,
          notes: sharedNotes,
        };
      case 'transfers':
        return {
          memberId,
          personName: personName.trim() || undefined,
          direction,
          transferDate: eventDate,
          otherChurchName: otherChurchName.trim() || null,
          letterReference: certificateNumber.trim() || null,
          notes: sharedNotes,
        };
    }
  }, [
    baptismType,
    canCreatePeople,
    certificateNumber,
    editDetails,
    dateOfDeath,
    direction,
    editingId,
    eventDate,
    firstName,
    kind,
    lastName,
    middleName,
    marriageType,
    memberId,
    ministerName,
    ministers,
    notes,
    otherChurchName,
    personName,
    peopleRows,
    place,
    spouseOneMemberId,
    spouseOneName,
    spouseOneWitnesses,
    spouseTwoMemberId,
    spouseTwoName,
    spouseTwoWitnesses,
  ]);

  const populateFromRow = (row: RegisterRow) => {
    setEditingId(row.id);
    setMemberId(row.memberId ?? null);
    setSpouseOneMemberId(row.spouseOneMemberId ?? null);
    setSpouseTwoMemberId(row.spouseTwoMemberId ?? null);
    setPersonName(row.personName ?? '');
    const nameParts =
      row.firstName || row.lastName
        ? {
            firstName: row.firstName ?? '',
            middleName: row.middleName ?? '',
            lastName: row.lastName ?? '',
          }
        : splitSacramentPersonName(row.personName ?? '');
    setFirstName(nameParts.firstName);
    setMiddleName(nameParts.middleName);
    setLastName(nameParts.lastName);
    setSpouseOneName(row.spouseOneName ?? '');
    setSpouseTwoName(row.spouseTwoName ?? '');
    setSpouseOneWitnesses(witnessDraftsFrom(row.spouseOneWitnesses));
    setSpouseTwoWitnesses(witnessDraftsFrom(row.spouseTwoWitnesses));
    setMinisters(ministerDraftsFrom(marriageMinisterNames(row)));
    setEventDate(kind === 'funerals' ? funeralDateFor(row) : dateFor(kind, row));
    setPlace(row.place ?? '');
    setMinisterName(row.ministerName ?? row.officiantName ?? '');
    setCertificateNumber(certificateFor(kind, row) ?? '');
    setBaptismType(row.baptismType ?? 'unknown');
    setMarriageType(row.marriageType ?? 'church');
    setDirection(row.direction ?? 'in');
    setOtherChurchName(row.otherChurchName ?? '');
    setDateOfDeath(row.dateOfDeath ?? null);
    setNotes(row.notes ?? '');
    setEditDetails({
      motherMemberId: row.motherMemberId ?? null,
      motherName: row.motherName ?? '',
      fatherMemberId: row.fatherMemberId ?? null,
      fatherName: row.fatherName ?? '',
      photoKey: row.photoKey ?? null,
      photoPreviewUrl: row.photoUrl ?? null,
      certificatePhotoKey: row.certificatePhotoKey ?? null,
      certificatePhotoPreviewUrl: row.certificatePhotoUrl ?? null,
      createMember: false,
    });
    setDetailsRowKey(null);
    setEditDetailsOpen(false);
    memberPicker.setSelected(
      row.memberId ? { id: row.memberId, label: row.personName ?? '' } : null,
    );
    spouseOnePicker.setSelected(
      row.spouseOneMemberId
        ? { id: row.spouseOneMemberId, label: row.spouseOneName ?? '' }
        : null,
    );
    spouseTwoPicker.setSelected(
      row.spouseTwoMemberId
        ? { id: row.spouseTwoMemberId, label: row.spouseTwoName ?? '' }
        : null,
    );
  };

  const invalidateRegister = () =>
    Promise.all([
      queryClient.invalidateQueries({ queryKey: ['sacraments', kind] }),
      queryClient.invalidateQueries({ queryKey: ['people'] }),
    ]);

  const create = useMutation({
    mutationFn: () =>
      api.post<RegisterRow | { data: RegisterRow[] }>(`/sacraments/${kind}`, body),
    onSuccess: async (result) => {
      const count =
        result && typeof result === 'object' && 'data' in result && Array.isArray(result.data)
          ? result.data.length
          : 1;
      const addedMembers = peopleRows.some((row) => row.createMember && !row.memberId);
      toast.success(
        addedMembers && canCreatePeople
          ? t(`${kind}.savedWithMembers`, { count })
          : count > 1
            ? t(`${kind}.savedMany`, { count })
            : t(`${kind}.saved`),
      );
      setOpen(false);
      reset();
      await invalidateRegister();
    },
    onError: (error) => toast.error(resolveApiError(error, tErrors)),
  });

  const update = useMutation({
    mutationFn: () => api.patch<RegisterRow>(`/sacraments/${kind}/${editingId}`, body),
    onSuccess: async (row) => {
      const linked =
        Boolean(row.memberId) || Boolean(row.spouseOneMemberId) || Boolean(row.spouseTwoMemberId);
      toast.success(linked ? t(`${kind}.updated`) : t(`${kind}.updatedStandalone`));
      setOpen(false);
      reset();
      await invalidateRegister();
    },
    onError: (error) => toast.error(resolveApiError(error, tErrors)),
  });

  const openEdit = (row: RegisterRow) => {
    populateFromRow(row);
    setDetailId(null);
    setOpen(true);
  };

  const columns: DataTableColumn<RegisterRow>[] = useMemo(() => {
    const base: DataTableColumn<RegisterRow>[] = [
      {
        id: 'date',
        header: t(`${kind}.columns.date`),
        cell: (row) => {
          const value = dateFor(kind, row);
          return value ? formatDate(value) : '—';
        },
      },
      {
        id: 'person',
        header: t(
          kind === 'marriages' ? 'marriages.columns.couple' : `${kind}.columns.person`,
        ),
        cell: (row) =>
          kind === 'marriages' ? (
            <span className="inline-flex flex-wrap items-center gap-x-1">
              <MemberNameLink
                memberId={row.spouseOneMemberId}
                name={row.spouseOneName ?? ''}
              />
              {row.spouseOneName && row.spouseTwoName ? <span aria-hidden>&</span> : null}
              <MemberNameLink
                memberId={row.spouseTwoMemberId}
                name={row.spouseTwoName ?? ''}
              />
            </span>
          ) : (
            <MemberNameLink memberId={row.memberId} name={row.personName ?? ''} />
          ),
      },
    ];

    if (kind === 'transfers') {
      base.push(
        {
          id: 'direction',
          header: t('transfers.columns.direction'),
          cell: (row) =>
            row.direction ? (
              <Badge variant="neutral">{t(`transfers.direction.${row.direction}`)}</Badge>
            ) : (
              '—'
            ),
        },
        {
          id: 'otherChurch',
          header: t('transfers.columns.otherChurch'),
          cell: (row) => row.otherChurchName || '—',
        },
        {
          id: 'reference',
          header: t('transfers.columns.reference'),
          cell: (row) => row.letterReference || '—',
        },
      );
      return base;
    }

    if (kind === 'funerals') {
      base.push(
        {
          id: 'funeralDate',
          header: t('funerals.columns.funeralDate'),
          cell: (row) => {
            const value = funeralDateFor(row);
            return value ? formatDate(value) : '—';
          },
        },
        {
          id: 'place',
          header: t('funerals.columns.place'),
          cell: (row) => row.place || '—',
        },
        {
          id: 'officiant',
          header: t('funerals.columns.officiant'),
          cell: (row) => row.officiantName || '—',
        },
      );
      return base;
    }

    base.push(
      {
        id: 'place',
        header: t(`${kind}.columns.place`),
        cell: (row) => row.place || '—',
      },
      {
        id: 'minister',
        header: t(kind === 'marriages' ? 'marriages.columns.ministers' : `${kind}.columns.minister`),
        cell: (row) => {
          if (kind === 'marriages') {
            const names = marriageMinisterNames(row);
            return names.length > 0 ? names.join(', ') : '—';
          }
          return row.ministerName || '—';
        },
      },
      {
        id: 'number',
        header: t(`${kind}.columns.number`),
        cell: (row) =>
          row.baptismNumber ||
          row.confirmationNumber ||
          row.certificateNumber ||
          '—',
      },
    );
    return base;
  }, [kind, t]);

  const rows = list.data?.data ?? [];
  const pageMeta = list.data?.page;
  const appliedCount =
    (search.trim() ? 1 : 0) +
    (filterDateRange.preset !== 'allTime' || filterDateRange.from || filterDateRange.to ? 1 : 0) +
    (supportsCertificateFilter(kind) && certificateQuery.trim() ? 1 : 0);

  const nameSearchLabel =
    kind === 'marriages' ? t('filters.searchCouple') : t('filters.searchName');
  const certificateSearchLabel =
    kind === 'transfers' ? t('filters.letterReference') : t('filters.certificateNumber');

  const dateFilterLabels = {
    today: tCommon('date.today'),
    yesterday: tCommon('date.yesterday'),
    thisWeek: tCommon('date.thisWeek'),
    lastWeek: tCommon('date.lastWeek'),
    thisMonth: tCommon('date.thisMonth'),
    lastMonth: tCommon('date.lastMonth'),
    thisQuarter: tCommon('date.thisQuarter'),
    thisYear: tCommon('date.thisYear'),
    lastYear: tCommon('date.lastYear'),
    allTime: tCommon('date.allTime'),
    custom: tCommon('date.custom'),
    from: t('filters.dateFrom'),
    to: t('filters.dateTo'),
  };
  const isEditing = Boolean(editingId);
  const showPeopleList = supportsMultiplePeople(kind) && !isEditing;
  const canSubmit =
    Boolean(eventDate) &&
    (kind === 'marriages'
      ? Boolean(spouseOneMemberId || spouseOneName.trim()) &&
        Boolean(spouseTwoMemberId || spouseTwoName.trim())
      : showPeopleList
        ? peopleRows.length > 0 &&
          peopleRows.every((row) =>
            Boolean(row.memberId || (row.firstName.trim() && row.lastName.trim())),
          )
        : kind === 'baptisms' || kind === 'confirmations'
          ? Boolean(memberId || (firstName.trim() && lastName.trim()))
          : Boolean(memberId || personName.trim()));
  const memberIsLinked = Boolean(
    memberId || spouseOneMemberId || spouseTwoMemberId ||
      peopleRows.some((row) => row.memberId),
  );

  return (
    <div>
      <PageHeader
        title={t(`${kind}.title`)}
        description={t(`${kind}.description`)}
        headerActions={
          canRecord ? (
            <Button
              onClick={() => {
                reset();
                setOpen(true);
              }}
            >
              {t(`${kind}.create`)}
            </Button>
          ) : null
        }
      />

      <SacramentsSubnav />

      <FilterBar
        appliedCount={appliedCount}
        onClearAll={() => {
          setSearch('');
          setCertificateQuery('');
          setFilterDateRange(emptyDateRange());
          setPage(1);
        }}
        labels={{
          filters: t('filters.title'),
          clearAll: t('filters.clearAll'),
          applied: (count) => t('filters.applied', { count }),
        }}
        leading={
          <SearchInput
            value={search}
            onChange={(value) => {
              setSearch(value);
              setPage(1);
            }}
            placeholder={nameSearchLabel}
            clearLabel={tCommon('search.clear')}
            className="max-w-none sm:w-64"
            aria-label={nameSearchLabel}
          />
        }
      >
        {({ popoverPortal }) => (
          <>
            <DateRangeFilter
              value={filterDateRange}
              onChange={(range) => {
                setFilterDateRange(range);
                setPage(1);
              }}
              popoverPortal={popoverPortal}
              labels={dateFilterLabels}
              presets={LIST_FILTER_DATE_RANGE_PRESETS}
            />
            {supportsCertificateFilter(kind) ? (
              <SearchInput
                value={certificateQuery}
                onChange={(value) => {
                  setCertificateQuery(value);
                  setPage(1);
                }}
                placeholder={certificateSearchLabel}
                clearLabel={tCommon('search.clear')}
                className="max-w-none sm:w-56"
                aria-label={certificateSearchLabel}
              />
            ) : null}
          </>
        )}
      </FilterBar>

      <DataTable
        rows={rows}
        columns={columns}
        rowKey={(row) => row.id}
        loading={list.isLoading}
        onRowClick={(row) => setDetailId(row.id)}
        empty={
          <p className="text-body-sm text-fg-secondary">
            {appliedCount > 0 ? t(`${kind}.emptyFiltered`) : t(`${kind}.empty`)}
          </p>
        }
      />

      {pageMeta && pageMeta.totalItems > 0 ? (
        <div className="mt-4">
          <Pagination
            page={Number(pageMeta.page)}
            pageSize={Number(pageMeta.pageSize)}
            totalItems={pageMeta.totalItems}
            totalPages={pageMeta.totalPages}
            onPageChange={setPage}
            onPageSizeChange={(next) => {
              setPageSize(next);
              setPage(1);
            }}
            labels={{
              showing: ({ from, to, total }) =>
                tCommon('pagination.showing', { from, to, total }),
              perPage: tCommon('pagination.perPage'),
              first: tCommon('pagination.first'),
              previous: tCommon('pagination.previous'),
              next: tCommon('pagination.next'),
              last: tCommon('pagination.last'),
            }}
          />
        </div>
      ) : null}

      <SacramentRegisterDetailDialog
        kind={kind}
        rowId={detailId}
        open={detailId !== null}
        canEdit={canEdit}
        canCreateMember={canEdit && canCreatePeople}
        onOpenChange={(next) => {
          if (!next) setDetailId(null);
        }}
        onEdit={openEdit}
      />

      <FormDialog
        open={open}
        onOpenChange={(next) => {
          if (!next && (detailsRowKey || editDetailsOpen)) return;
          setOpen(next);
          if (!next) reset();
        }}
        title={isEditing ? t(`${kind}.edit`) : t(`${kind}.create`)}
        description={isEditing && memberIsLinked ? t('fields.memberSyncHint') : undefined}
        submitLabel={isEditing ? t(`${kind}.saveEdit`) : t(`${kind}.save`)}
        cancelLabel={tCommon('action.cancel')}
        submitting={isEditing ? update.isPending : create.isPending}
        submitDisabled={!canSubmit}
        size={showPeopleList ? '2xl' : 'lg'}
        onSubmit={() => (isEditing ? update.mutate() : create.mutate())}
      >
        {kind === 'marriages' ? (
          <>
            <FormField label={t('marriages.fields.spouseOneMember')} required>
              {(field) => (
                <EntityCombobox
                  id={field.id}
                  value={spouseOneMemberId}
                  onChange={(id) => {
                    setSpouseOneMemberId(id);
                    spouseOnePicker.select(id);
                    if (id) {
                      const label =
                        spouseOnePicker.options.find((row) => row.id === id)?.label ??
                        spouseOnePicker.selected?.label;
                      if (label) setSpouseOneName(label);
                    }
                  }}
                  options={spouseOnePicker.options}
                  selected={spouseOnePicker.selected}
                  onQueryChange={spouseOnePicker.setQuery}
                  loading={spouseOnePicker.loading}
                  placeholder={t('memberPicker.placeholder')}
                  emptyMessage={t('memberPicker.empty')}
                  clearLabel={tCommon('action.clearSelection')}
                  popoverPortal="dialog"
                />
              )}
            </FormField>
            {!spouseOneMemberId ? (
              <FormField label={t('marriages.fields.spouseOneName')} required>
                {(field) => (
                  <Input
                    id={field.id}
                    value={spouseOneName}
                    onChange={(event) => setSpouseOneName(event.target.value)}
                  />
                )}
              </FormField>
            ) : null}
            <MarriageWitnessPair
              enabled={open}
              scope="sacrament-spouse-one-witness"
              witnesses={spouseOneWitnesses}
              onChange={setSpouseOneWitnesses}
              labelFor={(number) => t('marriages.fields.spouseOneWitness', { number })}
              nameLabelFor={(number) => t('marriages.fields.spouseOneWitnessName', { number })}
            />
            <FormField label={t('marriages.fields.spouseTwoMember')} required>
              {(field) => (
                <EntityCombobox
                  id={field.id}
                  value={spouseTwoMemberId}
                  onChange={(id) => {
                    setSpouseTwoMemberId(id);
                    spouseTwoPicker.select(id);
                    if (id) {
                      const label =
                        spouseTwoPicker.options.find((row) => row.id === id)?.label ??
                        spouseTwoPicker.selected?.label;
                      if (label) setSpouseTwoName(label);
                    }
                  }}
                  options={spouseTwoPicker.options}
                  selected={spouseTwoPicker.selected}
                  onQueryChange={spouseTwoPicker.setQuery}
                  loading={spouseTwoPicker.loading}
                  placeholder={t('memberPicker.placeholder')}
                  emptyMessage={t('memberPicker.empty')}
                  clearLabel={tCommon('action.clearSelection')}
                  popoverPortal="dialog"
                />
              )}
            </FormField>
            {!spouseTwoMemberId ? (
              <FormField label={t('marriages.fields.spouseTwoName')} required>
                {(field) => (
                  <Input
                    id={field.id}
                    value={spouseTwoName}
                    onChange={(event) => setSpouseTwoName(event.target.value)}
                  />
                )}
              </FormField>
            ) : null}
            <MarriageWitnessPair
              enabled={open}
              scope="sacrament-spouse-two-witness"
              witnesses={spouseTwoWitnesses}
              onChange={setSpouseTwoWitnesses}
              labelFor={(number) => t('marriages.fields.spouseTwoWitness', { number })}
              nameLabelFor={(number) => t('marriages.fields.spouseTwoWitnessName', { number })}
            />
          </>
        ) : showPeopleList ? null : (
          <>
            <FormField label={t('fields.member')} required={kind === 'funerals' || kind === 'transfers'}>
              {(field) => (
                <EntityCombobox
                  id={field.id}
                  value={memberId}
                  onChange={(id) => {
                    setMemberId(id);
                    memberPicker.select(id);
                    if (id) {
                      const option = memberPicker.options.find((row) => row.id === id);
                      const label = option?.label ?? memberPicker.selected?.label;
                      if (label) setPersonName(label);
                      if (option) {
                        setFirstName(option.firstName);
                        setMiddleName(option.middleName);
                        setLastName(option.lastName);
                      }
                    }
                  }}
                  options={memberPicker.options}
                  selected={memberPicker.selected}
                  onQueryChange={memberPicker.setQuery}
                  loading={memberPicker.loading}
                  placeholder={t('memberPicker.placeholder')}
                  emptyMessage={t('memberPicker.empty')}
                  clearLabel={tCommon('action.clearSelection')}
                  popoverPortal="dialog"
                />
              )}
            </FormField>
            {!memberId && (kind === 'baptisms' || kind === 'confirmations') ? (
              <div className="grid gap-4 sm:grid-cols-3">
                <FormField label={t('people.lastName')} required>
                  {(field) => (
                    <Input
                      id={field.id}
                      value={lastName}
                      onChange={(event) => setLastName(event.target.value)}
                    />
                  )}
                </FormField>
                <FormField label={t('people.firstName')} required>
                  {(field) => (
                    <Input
                      id={field.id}
                      value={firstName}
                      onChange={(event) => setFirstName(event.target.value)}
                    />
                  )}
                </FormField>
                <FormField label={t('people.middleName')}>
                  {(field) => (
                    <Input
                      id={field.id}
                      value={middleName}
                      onChange={(event) => setMiddleName(event.target.value)}
                    />
                  )}
                </FormField>
              </div>
            ) : !memberId ? (
              <FormField label={t(`${kind}.fields.personName`)} required>
                {(field) => (
                  <Input
                    id={field.id}
                    value={personName}
                    onChange={(event) => setPersonName(event.target.value)}
                  />
                )}
              </FormField>
            ) : null}
            <p className="text-caption text-fg-secondary">{t('fields.memberHint')}</p>
            {isEditing && (kind === 'baptisms' || kind === 'confirmations') ? (
              <SacramentImagesFields
                personName={[firstName, middleName, lastName].filter((part) => part.trim()).join(' ') || personName}
                value={editDetails}
                onChange={(patch) => setEditDetails((current) => ({ ...current, ...patch }))}
              />
            ) : null}
            {isEditing && (kind === 'baptisms' || kind === 'confirmations') ? (
              <div className="flex justify-end">
                <Button
                  type="button"
                  variant={sacramentPersonHasDetails(editDetails) ? 'secondary' : 'outline'}
                  onClick={() => setEditDetailsOpen(true)}
                >
                  {t('people.extraDetails')}
                </Button>
              </div>
            ) : null}
          </>
        )}

        {kind === 'baptisms' ? (
          <div className="grid items-start gap-4 sm:grid-cols-3">
            <FormField label={t('baptisms.fields.baptismDate')} required>
              {(field) => (
                <DatePicker
                  id={field.id}
                  value={eventDate}
                  onChange={setEventDate}
                  popoverPortal="dialog"
                />
              )}
            </FormField>
            <FormField label={t('baptisms.fields.place')}>
              {(field) => (
                <Input
                  id={field.id}
                  value={place}
                  onChange={(event) => setPlace(event.target.value)}
                />
              )}
            </FormField>
            <FormField label={t('baptisms.fields.baptismType')}>
              {(field) => (
                <Listbox
                  id={field.id}
                  value={baptismType}
                  onChange={(value) =>
                    setBaptismType((value as typeof baptismType) ?? 'unknown')
                  }
                  options={(['infant', 'adult', 'unknown'] as const).map((value) => ({
                    value,
                    label: t(`baptisms.type.${value}`),
                  }))}
                  popoverPortal="dialog"
                />
              )}
            </FormField>
          </div>
        ) : kind === 'funerals' ? (
          <>
            <FormField label={t('funerals.fields.dateOfDeath')} required>
              {(field) => (
                <DatePicker
                  id={field.id}
                  value={dateOfDeath}
                  onChange={setDateOfDeath}
                  popoverPortal="dialog"
                />
              )}
            </FormField>
            <FormField label={t('funerals.fields.serviceDate')}>
              {(field) => (
                <DatePicker
                  id={field.id}
                  value={eventDate}
                  onChange={setEventDate}
                  popoverPortal="dialog"
                />
              )}
            </FormField>
          </>
        ) : (
          <FormField
            label={t(
              kind === 'confirmations'
                ? 'confirmations.fields.confirmationDate'
                : kind === 'marriages'
                  ? 'marriages.fields.marriageDate'
                  : 'transfers.fields.transferDate',
            )}
            required
          >
            {(field) => (
              <DatePicker
                id={field.id}
                value={eventDate}
                onChange={setEventDate}
                popoverPortal="dialog"
              />
            )}
          </FormField>
        )}

        {kind === 'transfers' ? (
          <FormField label={t('transfers.fields.direction')} required>
            {(field) => (
              <Listbox
                id={field.id}
                value={direction}
                onChange={(value) => setDirection((value as 'in' | 'out') ?? 'in')}
                options={[
                  { value: 'in', label: t('transfers.direction.in') },
                  { value: 'out', label: t('transfers.direction.out') },
                ]}
                popoverPortal="dialog"
              />
            )}
          </FormField>
        ) : null}

        {kind !== 'transfers' && kind !== 'baptisms' ? (
          <FormField
            label={t(
              kind === 'funerals' ? 'funerals.fields.place' : `${kind}.fields.place`,
            )}
          >
            {(field) => (
              <Input
                id={field.id}
                value={place}
                onChange={(event) => setPlace(event.target.value)}
              />
            )}
          </FormField>
        ) : kind === 'transfers' ? (
          <FormField label={t('transfers.fields.otherChurchName')}>
            {(field) => (
              <Input
                id={field.id}
                value={otherChurchName}
                onChange={(event) => setOtherChurchName(event.target.value)}
              />
            )}
          </FormField>
        ) : null}

        {kind === 'marriages' ? (
          <MarriageMinistersFields enabled={open} ministers={ministers} onChange={setMinisters} />
        ) : null}

        {kind !== 'marriages' && kind !== 'transfers' ? (
          <FormField
            label={t(
              kind === 'funerals'
                ? 'funerals.fields.officiantName'
                : `${kind}.fields.ministerName`,
            )}
          >
            {(field) => (
              <MinisterCombobox
                id={field.id}
                value={ministerName}
                onChange={setMinisterName}
                enabled={open}
                placeholder={t('ministerPicker.placeholder')}
                emptyMessage={t('ministerPicker.empty')}
                guestLabel={(name) => t('ministerPicker.guest', { name })}
                clearLabel={tCommon('action.clearSelection')}
                popoverPortal="dialog"
              />
            )}
          </FormField>
        ) : null}

        {showPeopleList ? (
          <SacramentPeopleFields
            rows={peopleRows}
            onChange={setPeopleRows}
            memberOptions={memberPicker.options}
            memberLoading={memberPicker.loading}
            onMemberQueryChange={memberPicker.setQuery}
            certificateLabel={
              kind === 'baptisms'
                ? t('baptisms.fields.baptismNumber')
                : t('confirmations.fields.confirmationNumber')
            }
            clearLabel={tCommon('action.clearSelection')}
            hint={t('people.hint')}
            allowCreateMember={canCreatePeople}
            onOpenDetails={(key) => {
              setEditDetailsOpen(false);
              setDetailsRowKey(key);
            }}
          />
        ) : null}

        {kind === 'marriages' ? (
          <FormField label={t('marriages.fields.marriageType')}>
            {(field) => (
              <Listbox
                id={field.id}
                value={marriageType}
                onChange={(value) =>
                  setMarriageType((value as typeof marriageType) ?? 'church')
                }
                options={(['church', 'civil', 'customary', 'other'] as const).map(
                  (value) => ({
                    value,
                    label: t(`marriages.type.${value}`),
                  }),
                )}
                popoverPortal="dialog"
              />
            )}
          </FormField>
        ) : null}

        {kind !== 'funerals' && !showPeopleList ? (
          <FormField
            label={t(
              kind === 'baptisms'
                ? 'baptisms.fields.baptismNumber'
                : kind === 'confirmations'
                  ? 'confirmations.fields.confirmationNumber'
                  : kind === 'marriages'
                    ? 'marriages.fields.certificateNumber'
                    : 'transfers.fields.letterReference',
            )}
          >
            {(field) => (
              <Input
                id={field.id}
                value={certificateNumber}
                onChange={(event) => setCertificateNumber(event.target.value)}
              />
            )}
          </FormField>
        ) : null}

        <FormField label={t(`${kind}.fields.notes`)}>
          {(field) => (
            <Textarea
              id={field.id}
              value={notes}
              onChange={(event) => setNotes(event.target.value)}
              rows={3}
            />
          )}
        </FormField>
      </FormDialog>

      <SacramentPersonDetailsDialog
        key={detailsRowKey ?? (editDetailsOpen ? 'edit' : 'closed')}
        open={detailsRowKey !== null || editDetailsOpen}
        personName={
          detailsRowKey
            ? composeSacramentPersonName(
                peopleRows.find((row) => row.key === detailsRowKey) ?? emptySacramentPersonRow(),
              )
            : [firstName, middleName, lastName].filter((part) => part.trim()).join(' ') || personName
        }
        allowCreateMember={canCreatePeople && detailsRowKey !== null}
        memberLinked={Boolean(
          detailsRowKey && peopleRows.find((row) => row.key === detailsRowKey)?.memberId,
        )}
        value={
          detailsRowKey
            ? peopleRows.find((row) => row.key === detailsRowKey) ?? emptySacramentPersonDetails()
            : editDetails
        }
        onOpenChange={(next) => {
          if (next) return;
          setDetailsRowKey(null);
          setEditDetailsOpen(false);
        }}
        onSave={(next) => {
          if (detailsRowKey) {
            setPeopleRows((rows) =>
              rows.map((row) =>
                row.key === detailsRowKey
                  ? {
                      ...row,
                      ...next,
                    }
                  : row,
              ),
            );
            const previous = peopleRows.find((row) => row.key === detailsRowKey);
            if (
              previous?.photoPreviewUrl &&
              previous.photoPreviewUrl !== next.photoPreviewUrl
            ) {
              revokePreview(previous.photoPreviewUrl);
            }
            if (
              previous?.certificatePhotoPreviewUrl &&
              previous.certificatePhotoPreviewUrl !== next.certificatePhotoPreviewUrl
            ) {
              revokePreview(previous.certificatePhotoPreviewUrl);
            }
          } else {
            if (editDetails.photoPreviewUrl && editDetails.photoPreviewUrl !== next.photoPreviewUrl) {
              revokePreview(editDetails.photoPreviewUrl);
            }
            if (
              editDetails.certificatePhotoPreviewUrl &&
              editDetails.certificatePhotoPreviewUrl !== next.certificatePhotoPreviewUrl
            ) {
              revokePreview(editDetails.certificatePhotoPreviewUrl);
            }
            setEditDetails(next);
          }
          setDetailsRowKey(null);
          setEditDetailsOpen(false);
        }}
      />
    </div>
  );
};
